import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { ChatNudgeCard } from '@wundo/ui';
import type { OnboardingNudgeMessageSource } from '@wundo/shared/onboardingNudge.ts';
import { AddMeetingDialog } from '@/components/agenda/AddMeetingDialog.tsx';
import { chatNudgeCopy } from '@/components/chatNudgeCopy.ts';
import { chatNudgeIcon } from '@/components/chatNudgeIcon.tsx';
import type { ChatPageNudge } from '@/lib/chatPageOffer.ts';
import { openMeetingSourcesPanel } from '@/lib/meetingSourcesPanel.ts';
import {
  dismissOnboardingNudge,
  reportOnboardingNudgeClicked,
} from '@/lib/onboardingNudge.ts';
import { m } from '@/paraglide/messages.js';

export interface ChatNudgeProps {
  nudge: ChatPageNudge;
  /** Whose nudge it is. Undefined while the user is still being read. */
  userId: string | undefined;
  /** Whether the button can still be pressed — a send in flight holds it. */
  disabled?: boolean;
  /**
   * Say something in the drawer. Two of the seven nudges are questions for
   * Wundo, and they carry what they were so the backend can close them.
   */
  onSend: (text: string, source?: OnboardingNudgeMessageSource) => void;
  /** The card is gone: refused, or taken up somewhere it cannot come back from. */
  onSettled: () => void;
}

/**
 * The one recommendation an empty drawer may carry, and what its button does.
 *
 * Three shapes of button, decided by which nudge it is. Some of them ask Wundo
 * something; two of those say so on the message they send — that tag is the
 * whole exit for those two, because a question leaves no row anywhere, while
 * asking Wundo to turn the notetaker on is closed by the row it writes. The rest
 * lead somewhere: a dialog, another page, or a panel on the page already behind
 * the drawer. Everything that is not closed by its own tag says it was taken up
 * in its own request, because what follows is not this component's to watch.
 *
 * Nothing is created by drawing this and nothing is stored. Refusing it is a
 * POST and a card that goes away.
 */
export function ChatNudge({
  nudge,
  userId,
  disabled = false,
  onSend,
  onSettled,
}: ChatNudgeProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  /* Only ever for the first nudge, which has no page to send anybody to. */
  const [adding, setAdding] = useState(false);

  const copy = chatNudgeCopy(nudge.key);

  const dismiss = useMutation({
    mutationFn: async () => await dismissOnboardingNudge(userId!, nudge.key),
    /*
     * Gone either way. A refusal that fails to reach the server is still a
     * refusal on this screen — the card standing there after the ✕ was clicked
     * is the one outcome nobody would forgive.
     */
    onSettled: async () => {
      onSettled();
      await queryClient.invalidateQueries({ queryKey: ['onboardingNudge'] });
    },
  });

  const clicked = useMutation({
    mutationFn: async () => await reportOnboardingNudgeClicked(userId!, nudge.key),
  });

  /*
   * Counted here rather than where the button leads: the nudge is what was
   * clicked, and what follows may be a page this drawer never hears about
   * again. A failed count is swallowed — it is bookkeeping, and the user is
   * already on their way.
   */
  const count = () => {
    if (userId) clicked.mutate();
  };

  const act = () => {
    switch (nudge.key) {
      case 'plan_first_meeting':
        count();
        setAdding(true);
        return;
      case 'try_library_roleplay':
        count();
        void navigate({ to: '/practice' });
        return;
      /*
       * Asked rather than navigated to: Wundo turns the notetaker on itself now,
       * for the meeting it finds. Counted here all the same — this one leaves
       * no source tag, because it is closed by the row the switch writes and
       * not by having been asked.
       */
      case 'enable_notetaker':
        count();
        onSend(m.nudge_enable_notetaker_message());
        return;
      case 'upload_person_document':
        count();
        /*
         * Nowhere to send anybody: the place to hand a document over is the
         * panel on the page this drawer is already standing beside.
         */
        openMeetingSourcesPanel();
        return;
      /*
       * The same words the pill beside it says, so the conversation reads as
       * one thing whichever of the two was clicked.
       */
      case 'rehearse_this_meeting':
        onSend(m.chat_quick_prep_roleplay());
        return;
      case 'ask_conversation_tips':
        onSend(m.chat_quick_prep_tips(), 'conversation_tips');
        return;
      case 'ask_meeting_feedback':
        onSend(m.chat_quick_report_check(), 'meeting_feedback');
    }
  };

  return (
    <>
      <ChatNudgeCard
        icon={chatNudgeIcon(nudge.key)}
        title={copy.title}
        body={copy.body}
        actionLabel={copy.action}
        onAction={act}
        dismissLabel={m.nudge_dismiss()}
        onDismiss={() => (userId ? dismiss.mutate() : onSettled())}
        disabled={disabled || dismiss.isPending}
      />
      {userId && (
        <AddMeetingDialog
          userId={userId}
          open={adding}
          onClose={() => setAdding(false)}
        />
      )}
    </>
  );
}
